import { useState, useCallback, useEffect } from 'react'
import { Button } from '../common'

interface CustomDurationModalProps {
  isOpen: boolean
  onClose: () => void
  onSave: (duration: { label: string; weeks: number; description: string }) => void
  editingDuration?: { label: string; weeks: number; description: string } | null
}

type DurationUnit = 'weeks' | 'months'

export function CustomDurationModal({
  isOpen,
  onClose,
  onSave,
  editingDuration,
}: CustomDurationModalProps) {
  const [label, setLabel] = useState('')
  const [value, setValue] = useState<number>(4)
  const [unit, setUnit] = useState<DurationUnit>('weeks')
  const [description, setDescription] = useState('')

  // Reset form when modal opens/closes or editing duration changes
  useEffect(() => {
    if (isOpen) {
      if (editingDuration) {
        setLabel(editingDuration.label)
        // Convert weeks to appropriate unit
        if (editingDuration.weeks >= 4 && editingDuration.weeks % 4 === 0) {
          setUnit('months')
          setValue(editingDuration.weeks / 4)
        } else {
          setUnit('weeks')
          setValue(editingDuration.weeks)
        }
        setDescription(editingDuration.description)
      } else {
        setLabel('')
        setValue(4)
        setUnit('weeks')
        setDescription('')
      }
    }
  }, [isOpen, editingDuration])

  const handleSave = useCallback(() => {
    if (!label.trim() || value <= 0) return

    const weeks = unit === 'months' ? value * 4 : value
    onSave({
      label: label.trim(),
      weeks,
      description: description.trim(),
    })
    onClose()
  }, [label, value, unit, description, onSave, onClose])

  const isValid = label.trim().length > 0 && value > 0

  if (!isOpen) return null

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black/50"
        onClick={onClose}
      />

      {/* Modal */}
      <div className="relative bg-white rounded-xl shadow-xl w-full max-w-md p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">
          {editingDuration ? 'Edit Duration' : 'Add Custom Duration'}
        </h3>

        <div className="space-y-4">
          {/* Label */}
          <div>
            <label htmlFor="duration-label" className="block text-sm font-medium text-gray-700 mb-1">
              Label <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              id="duration-label"
              value={label}
              onChange={(e) => setLabel(e.target.value)}
              placeholder="e.g., 6 Weeks, 2 Months"
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              maxLength={30}
              autoFocus
            />
          </div>

          {/* Duration Value + Unit */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Duration <span className="text-red-500">*</span>
            </label>
            <div className="flex gap-2">
              <input
                type="number"
                min={1}
                max={unit === 'months' ? 24 : 104}
                value={value}
                onChange={(e) => setValue(Math.max(1, parseInt(e.target.value) || 1))}
                className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />
              <select
                value={unit}
                onChange={(e) => {
                  const newUnit = e.target.value as DurationUnit
                  // Convert value when switching units
                  if (newUnit === 'months' && unit === 'weeks') {
                    setValue(Math.max(1, Math.round(value / 4)))
                  } else if (newUnit === 'weeks' && unit === 'months') {
                    setValue(value * 4)
                  }
                  setUnit(newUnit)
                }}
                className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white"
              >
                <option value="weeks">Weeks</option>
                <option value="months">Months</option>
              </select>
            </div>
            <p className="mt-1 text-xs text-gray-500">
              {unit === 'months' ? `${value * 4} weeks` : `${value} week${value !== 1 ? 's' : ''}`}
            </p>
          </div>

          {/* Description */}
          <div>
            <label htmlFor="duration-description" className="block text-sm font-medium text-gray-700 mb-1">
              Description <span className="text-gray-400">(optional)</span>
            </label>
            <input
              type="text"
              id="duration-description"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="e.g., Post-surgery recovery"
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              maxLength={50}
            />
          </div>
        </div>

        {/* Actions */}
        <div className="flex gap-3 mt-6">
          <Button variant="outline" onClick={onClose} fullWidth>
            Cancel
          </Button>
          <Button onClick={handleSave} disabled={!isValid} fullWidth>
            {editingDuration ? 'Save Changes' : 'Add Duration'}
          </Button>
        </div>
      </div>
    </div>
  )
}
